/**
 * Preview Pagination Component
 * A simple pagination component for the Preview Questions page
 * matching the Figma design
 */

import React from "react";
import { useResources } from "../../hooks/useResources";
import "./PreviewPagination.scss";

interface PreviewPaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  maxVisiblePages?: number;
}

const PreviewPagination: React.FC<PreviewPaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  maxVisiblePages = 4,
}) => {
  const { getResourceValue } = useResources();

  if (totalPages <= 1) {
    return null;
  }

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  };

  // Calculate visible page numbers
  const getVisiblePages = (): number[] => {
    const pages: number[] = [];
    
    if (totalPages <= maxVisiblePages) {
      // Show all pages if total is less than max visible
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Calculate start and end for the sliding window
      let start = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
      let end = start + maxVisiblePages - 1;

      // Adjust if we're near the end
      if (end > totalPages) {
        end = totalPages;
        start = Math.max(1, end - maxVisiblePages + 1);
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
    }

    return pages;
  };

  const visiblePages = getVisiblePages();
  const isPreviousDisabled = currentPage === 1;
  const isNextDisabled = currentPage >= totalPages;

  return (
    <div className="preview-pagination">
      <button
        className={`preview-pagination__nav ${isPreviousDisabled ? "preview-pagination__nav--disabled" : ""}`}
        onClick={handlePreviousPage}
        disabled={isPreviousDisabled}
        aria-label={getResourceValue("previous", "Previous")}
      >
        {getResourceValue("previous", "Previous")}
      </button>

      <div className="preview-pagination__pages">
        {visiblePages.map((page) => (
          <button
            key={page}
            className={`preview-pagination__page ${
              page === currentPage ? "preview-pagination__page--active" : ""
            }`}
            onClick={() => onPageChange(page)}
            aria-label={`${getResourceValue("page", "Page")} ${page}`}
            aria-current={page === currentPage ? "page" : undefined}
          >
            {page}
          </button>
        ))}
      </div>

      <button
        className={`preview-pagination__nav ${isNextDisabled ? "preview-pagination__nav--disabled" : ""}`}
        onClick={handleNextPage}
        disabled={isNextDisabled}
        aria-label={getResourceValue("next", "Next")}
      >
        {getResourceValue("next", "Next")}
      </button>
    </div>
  );
};

export default PreviewPagination;

